/**
 * Ordningen i leadets aktivitetslogg: nyast datum först. Aktiviteter samma
 * dag kan dras i ordning; ordningen sparas i fältet `ordning` på
 * crmLeads/{id}/activities/{aktivitetId} och gäller bara inom dagen.
 *
 * Inom en dag: aktiviteter utan ordning (nya sedan senaste dragningen)
 * först, nyast registrerad överst, sedan de dragna i sin ordning.
 */
import { toMillis } from './pipelineFlode';

const harOrdning = a => typeof a.ordning === 'number';

function inomDag(a, b) {
  if (harOrdning(a) !== harOrdning(b)) return harOrdning(a) ? 1 : -1;
  if (harOrdning(a)) return a.ordning - b.ordning;
  return (toMillis(b.createdAt) ?? 0) - (toMillis(a.createdAt) ?? 0);
}

export function sorteraAktiviteter(lista) {
  return [...lista].sort((a, b) => (b.date || '').localeCompare(a.date || '') || inomDag(a, b));
}

/** Kan aktiviteten dras – finns det fler aktiviteter samma dag? */
export function kanDras(lista, aktivitet) {
  return lista.filter(a => (a.date || '') === (aktivitet.date || '')).length > 1;
}

/**
 * Flyttar en aktivitet till en annans plats inom samma dag.
 * @returns {{ lista, uppdateringar: [{ id, ordning }] } | null}
 *   null när de har olika datum eller inget ändras. uppdateringar: hela
 *   dagens nya ordning (0, 1, 2 …), bara de som ändrats.
 */
export function flyttaInomDag(lista, aktivId, overId) {
  if (!aktivId || !overId || aktivId === overId) return null;
  const sorterad = sorteraAktiviteter(lista);
  const aktiv = sorterad.find(a => a.id === aktivId);
  const over = sorterad.find(a => a.id === overId);
  if (!aktiv || !over || (aktiv.date || '') !== (over.date || '')) return null;

  const dag = sorterad.filter(a => (a.date || '') === (aktiv.date || ''));
  const fran = dag.findIndex(a => a.id === aktivId);
  const till = dag.findIndex(a => a.id === overId);
  const ny = [...dag];
  ny.splice(till, 0, ny.splice(fran, 1)[0]);

  const uppdateringar = ny
    .map((a, ordning) => ({ id: a.id, ordning }))
    .filter(u => dag.find(a => a.id === u.id).ordning !== u.ordning);
  const nyOrdning = new Map(ny.map((a, i) => [a.id, i]));
  return {
    lista: sorteraAktiviteter(lista.map(a => (nyOrdning.has(a.id) ? { ...a, ordning: nyOrdning.get(a.id) } : a))),
    uppdateringar,
  };
}
